import { REDO_OUTBOUND_CARRIER_IDS } from "../rate-table/redo-carriers";
import { Carriers } from "./fulfillment-carriers-and-services";
import { OutboundShipmentAddress } from "./outbound-shipment";

export type OverboxService = "apc" | "epg";

/** First-mile carriers an overbox outer label can be bought on. */
export type OverboxCarrier = "fedex" | "ups" | "dhl";

/**
 * Third-party billing target for an overbox first-mile label. The label is
 * rated/created on Redo's carrier account (`OVERBOX_RATING_CARRIER_ACCOUNTS`);
 * EasyPost redirects the charge here via a `THIRD_PARTY` payment option.
 */
export interface OverboxThirdPartyBilling {
  /** The overbox carrier's (APC/EPG) FedEx/UPS account number to bill. */
  account: string;
  /** Billing account postal code — required by EasyPost THIRD_PARTY payment. */
  postalCode: string;
  /** Billing account country — required by EasyPost THIRD_PARTY payment. */
  country: string;
}

/** Per-first-mile-carrier third-party billing accounts. */
export interface OverboxBillingAccounts {
  fedex?: OverboxThirdPartyBilling;
  ups?: OverboxThirdPartyBilling;
  dhl?: OverboxThirdPartyBilling;
}

export interface OverboxFulfillmentCenterConfig {
  /** Display label for admin/merchant UI (e.g. "EWR"). */
  label: string;
  /** Induction address — where the outer label ships to. */
  inductionAddress: OutboundShipmentAddress;
  /**
   * Optional location-level billing overrides. When a carrier is set here it
   * takes precedence over the service-level billing account for this FC.
   */
  billingAccounts?: OverboxBillingAccounts;
}

export interface OverboxServiceConfig {
  /**
   * Overboxing-carrier-level (service-wide) third-party billing accounts — the
   * default for every fulfillment center that doesn't override them at the
   * location level.
   */
  billingAccounts?: OverboxBillingAccounts;
  fulfillmentCenters: Record<string, OverboxFulfillmentCenterConfig>;
}

/**
 * Redo's EasyPost carrier accounts that rate/create overbox first-mile labels.
 * The label is created on these accounts; EasyPost redirects the charge to the
 * overbox carrier's account via third-party billing (see
 * `getOverboxBillingAccount`).
 */
export const OVERBOX_RATING_CARRIER_ACCOUNTS: Partial<
  Record<OverboxCarrier, string>
> = {
  fedex: REDO_OUTBOUND_CARRIER_IDS.FEDEX,
  ups: REDO_OUTBOUND_CARRIER_IDS.B2B_UPS,
};

/**
 * Per-service overbox config: each fulfillment center's induction address plus
 * the overbox carrier's third-party billing accounts. Billing accounts resolve
 * at two levels — a service-level default and an optional per-fulfillment-
 * center override (see `getOverboxBillingAccount`).
 *
 * NOTE: induction addresses below are the carriers' publicly-listed facilities;
 * operations must confirm which facility Redo's account actually inducts at.
 */
export const OVERBOX_ACCOUNTS: Record<OverboxService, OverboxServiceConfig> = {
  apc: {
    // TODO: fill in APC's FedEx/UPS account numbers + billing zips.
    billingAccounts: {},
    fulfillmentCenters: {},
  },
  epg: {
    // TODO: fill in ePost Global's FedEx/UPS account numbers + billing zips.
    billingAccounts: {
      ups: { account: "R23123", postalCode: "07606", country: "US" },
      fedex: { account: "772849435", postalCode: "07606", country: "US" },
    },
    fulfillmentCenters: {
      lax: {
        label: "LAX",
        inductionAddress: {
          name: "ATTN: Receiving",
          company: "ePost Global",
          street1: "11137 Warland Drive",
          city: "Cypress",
          state: "CA",
          zip: "90630",
          country: "US",
          phone: "310-784-8485",
        },
      },
      ord: {
        label: "ORD",
        inductionAddress: {
          name: "ATTN: Receiving",
          company: "ePost Global",
          street1: "225 South Prospect Ave",
          city: "Itasca",
          state: "IL",
          zip: "60143",
          country: "US",
          phone: "310-784-8485",
        },
      },
      ewr: {
        label: "EWR",
        inductionAddress: {
          name: "ATTN: Receiving",
          company: "ePost Global",
          street1: "18 Central Boulevard",
          city: "South Hackensack",
          state: "NJ",
          zip: "07606",
          country: "US",
          phone: "310-784-8485",
        },
      },
    },
  },
};

export function getOverboxFulfillmentCenter(
  service: OverboxService,
  fc: string,
): OverboxFulfillmentCenterConfig | undefined {
  return OVERBOX_ACCOUNTS[service]?.fulfillmentCenters[fc];
}

export function listOverboxFulfillmentCenters(
  service: OverboxService,
): { id: string; label: string }[] {
  return Object.entries(OVERBOX_ACCOUNTS[service].fulfillmentCenters).map(
    ([id, config]) => ({ id, label: config.label }),
  );
}

/**
 * Resolves the third-party billing account for an overbox label. A
 * fulfillment-center-level (location) account overrides the service-level
 * (overboxing-carrier) account; either level may supply it.
 */
export function getOverboxBillingAccount(
  service: OverboxService,
  fc: string,
  carrier: OverboxCarrier,
): OverboxThirdPartyBilling | undefined {
  const fcConfig = getOverboxFulfillmentCenter(service, fc);
  return (
    fcConfig?.billingAccounts?.[carrier] ??
    OVERBOX_ACCOUNTS[service]?.billingAccounts?.[carrier]
  );
}

/** EasyPost service codes used to buy the outer ground label per carrier. */
export const OVERBOX_GROUND_SERVICE: Record<OverboxCarrier, string> = {
  fedex: "FEDEX_GROUND",
  ups: "Ground",
  // TODO: verify against the real DHL EasyPost account once one is configured.
  dhl: "Ground",
};

/** Ordered list of carriers for rendering the print-label button group. */
export const OVERBOX_CARRIERS: OverboxCarrier[] = ["fedex", "ups", "dhl"];

export const OVERBOX_CARRIER_LABELS: Record<OverboxCarrier, string> = {
  fedex: "FedEx",
  ups: "UPS",
  dhl: "DHL",
};

const OVERBOX_SERVICE_CARRIERS: Record<OverboxService, Carriers> = {
  apc: Carriers.APC,
  epg: Carriers.EPOST_GLOBAL,
};

/**
 * Membership check for overboxing carriers, used by the EOD manifest
 * create-only dispatch in create-eod-manifest-for-groups.ts.
 */
export function isOverboxingCarrier(carrier: string): boolean {
  return Object.values(OVERBOX_SERVICE_CARRIERS).includes(carrier as Carriers);
}
